import { useState, useEffect, useCallback } from 'react';
import { testimonials } from '@/mocks/testimonials';

export default function TestimonialsSection() {
  const [active, setActive] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const next = useCallback(() => {
    setActive((prev) => (prev + 1) % testimonials.length);
  }, []);

  const prev = useCallback(() => {
    setActive((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(next, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, next]);

  const t = testimonials[active];

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold mb-4">
            <i className="ri-star-fill text-xs"></i>
            Testimonios
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
            Lo que dicen las{' '}
            <span className="text-primary-600">radios y oyentes</span>
          </h2>
          <p className="mt-3 text-foreground-500 text-base max-w-xl mx-auto">
            Locutores, dueños de radios y oyentes comparten cómo el ecosistema RFW transformó su manera de vivir la radio.
          </p>
        </div>

        {/* Main testimonial card */}
        <div
          className="relative max-w-3xl mx-auto bg-background-50 rounded-3xl border border-background-200 p-8 md:p-10"
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
        >
          {/* Quote icon */}
          <div className="absolute -top-4 -left-4 w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center shadow-lg shadow-primary-500/20">
            <i className="ri-double-quotes-l text-background-50 text-lg"></i>
          </div>

          <div className="flex flex-col md:flex-row items-start gap-6">
            {/* Avatar + info */}
            <div className="flex flex-col items-center gap-3 shrink-0">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden bg-background-100 border-2 border-background-200">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="text-center">
                <p className="text-sm font-bold text-foreground-950">{t.name}</p>
                <p className="text-[10px] text-foreground-500 mt-0.5">{t.role}</p>
                {t.stationName && (
                  <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-[9px] font-semibold">
                    <i className="ri-radio-line text-[9px]"></i>
                    {t.stationName}
                  </span>
                )}
              </div>
              {/* Stars */}
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i
                    key={i}
                    className={`${
                      i < t.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'
                    } text-sm`}
                  ></i>
                ))}
              </div>
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="text-foreground-700 leading-relaxed text-sm md:text-base italic">
                &ldquo;{t.text}&rdquo;
              </p>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-background-100">
            <div className="flex items-center gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setActive(i);
                    setIsAutoPlaying(false);
                  }}
                  className={`rounded-full transition-all duration-300 cursor-pointer ${
                    i === active
                      ? 'w-8 h-2 bg-primary-500'
                      : 'w-2 h-2 bg-foreground-300 hover:bg-foreground-400'
                  }`}
                ></button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={prev}
                className="w-9 h-9 rounded-full bg-background-100 border border-background-200 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"
              >
                <i className="ri-arrow-left-line text-foreground-600 text-sm"></i>
              </button>
              <button
                onClick={next}
                className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center cursor-pointer hover:bg-primary-600 transition-colors"
              >
                <i className="ri-arrow-right-line text-background-50 text-sm"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mini grid of all testimonials */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 mt-8 max-w-3xl mx-auto">
          {testimonials.map((item, i) => (
            <button
              key={item.id}
              onClick={() => {
                setActive(i);
                setIsAutoPlaying(false);
              }}
              className={`text-left p-3 rounded-xl border transition-all duration-200 cursor-pointer ${
                i === active
                  ? 'bg-primary-50 border-primary-300 ring-1 ring-primary-200'
                  : 'bg-background-50 border-background-200 hover:border-primary-200'
              }`}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg overflow-hidden bg-background-100 shrink-0">
                  <img
                    src={item.avatar}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold text-foreground-800 truncate">{item.name}</p>
                  <p className="text-[9px] text-foreground-400 truncate">
                    {item.category === 'oyente' ? 'Oyente' : item.category === 'locutor' ? 'Locutor' : 'Dueño'}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}